/* eslint-disable react/jsx-no-comment-textnodes */
import { Box, Flex, HStack, IconButton, useDisclosure, Text, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Link, Image } from '@chakra-ui/react';
import { AlignJustify } from 'lucide-react';
import { motion } from 'framer-motion';
import React from 'react';
import { Link as RouterLink, NavLink as RouterNavLink } from 'react-router-dom';
import TextLogoWhite from '../images/TextLogoWhite.svg';
import ShortLogoBlue from '../images/ShortLogoBlue.svg';

interface Props {
    children: React.ReactNode;
    to: string;
	onClose?: () => void;
}

const Links = {
    'Home': '/',
    'About': '/about',
	'Cases': '/cases',
	'Resources': '/resources',
	'Reviews': '/reviews',
	'FAQ': '/faq',
    'Contact': '/#contact', // Section at the bottom of the home page
};

const NavLink = (props: Props) => {
	const { children, to, onClose } = props;
	return (
		<Link
			as={RouterNavLink}
			to={to}
			end
			px={{ base: 2, lg: 0 }}
			py={1}
			rounded={'md'}
			_hover={{
				textDecoration: 'none',
				color: 'grey.200',
			}}
			fontFamily={"Montserrat"}
			transitionDuration={"0.2s"}
			// Close the drawer
			onClick={onClose}
		>
			{children}
		</Link>
	);
};


export default function NavBar() {

    const { isOpen, onOpen, onClose } = useDisclosure();
    const btnRef = React.useRef<HTMLButtonElement | null>(null);

    // Animation Variants for the Navbar containers
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    // Animation Variants for the container items
    const itemVariants = {
        hidden: {
            opacity: 0, y: -50
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 500,
                damping: 36
            }
        },
    };
  
    return (
      <>
        {/* The full link row needs ~1000px; narrower screens (phones, portrait iPads) use the drawer */}
        <Box w={{ base: "100%", lg: "90%" }} paddingY={"10px"}>
  
            <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>

				{/* Open Menu Button */}
				<IconButton
					icon={<AlignJustify />}
					position={'absolute'}
					left={5}
					top={5}
					ref={btnRef}
					aria-label={'Open Menu'}
					display={{ lg: 'none' }}
					onClick={isOpen ? onClose : onOpen}
					alignItems={'center'}
					justifyContent={'center'}
					padding={2}
					backgroundColor={"transparent"}
					color={"yellow.100"}
					_hover={{ backgroundColor: "transparent" }}
					_active={{ backgroundColor: "transparent" }}
				/>

            <Flex
				alignItems={'center'}
				justifyContent={{ base: "center", lg: "flex-start" }}
				width={"100%"}
				// Keep clear space between the logo and the links
				marginRight={{ lg: 8 }}
			>
                <Link
					as={RouterLink}
					to="/"
					_hover={{
						textDecoration: 'none',
					}}
				>
					<Image
						src={TextLogoWhite}
						alt="Outlaw Defense Logo"
						width={"200px"}
						// Never squeeze the logo to make room for the links
						flexShrink={0}
						maxWidth={"none"}
					/>
				</Link>
            </Flex>

            <HStack
				alignItems={'center'}
			>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
					<HStack
						display={{ base: 'none', lg: 'flex' }}
						// A little tighter on small laptops so the row fits
						spacing={{ lg: 6, xl: 8 }}
						color={"white.200"}
						fontFamily={"Montserrat"}
						fontWeight={"600"}
					>
						{Object.entries(Links).map(([text, href]) => (
							<motion.div key={text} variants={itemVariants}>
								<NavLink to={href}>{text}</NavLink>
							</motion.div>
						))}
					</HStack>
                </motion.div>
            </HStack>

        </Flex>
  
            {/* Don't return focus to the menu button on close: focusing it would scroll back to the top */}
            <Drawer isOpen={isOpen} placement='left' onClose={onClose} finalFocusRef={btnRef} returnFocusOnClose={false}>

                <DrawerOverlay />

                <DrawerContent backgroundColor={"white.200"}>

                    <DrawerCloseButton />

                    <DrawerHeader>
                        <Link
							as={RouterLink}
							to="/"
							_hover={{
								textDecoration: 'none',
							}}
							onClick={onClose}
						>
							<Image
								src={ShortLogoBlue}
								alt="Outlaw Defense Logo"
								width={{ base: "200px", md: "280px" }}
							/>
						</Link>
                    </DrawerHeader>

                    <DrawerBody>
                        <Flex flexDirection="column" gap={4}>
                        {Object.entries(Links).map(([text, href]) => (
                            <NavLink key={text} to={href} onClose={onClose}>{text}</NavLink>
                        ))}
                        <Text fontFamily={"Montserrat"} fontWeight="600" textAlign={"left"} color={"black.200"}>
                            © Joe Wells 2025. All rights reserved.
                        </Text>
                        </Flex>
                    </DrawerBody>

                </DrawerContent>

            </Drawer>

        </Box>
      </>
    );
  }
  